/* eslint-disable quote-props */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/quotes */
import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, of, throwError, from } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  retry,
  switchMap,
} from 'rxjs/operators';
import { UtilsService } from './utils.service';
import { PayarenaService } from './payarena.service';
import { TokenRefreshService } from './token-refresh.service';
import { LoadingService } from './loading.service';

// A request is retried at most once after Flutter supplies a fresh access token.
// HttpContext is local to Angular and is never sent as an HTTP header.
const NATIVE_TOKEN_RETRY = new HttpContextToken<boolean>(() => false);

interface NativeTokenResult {
  identityToken?: string;
  accessToken?: string;
  refreshToken?: string;
  identityId?: string;
  retry?: boolean;
  requiresLogin?: boolean;
  error?: 'not_allowed';
}

type NativeTokenState =
  | { status: 'token'; token: string }
  | { status: 'requiresLogin' }
  | { status: 'notAllowed' }
  | { status: 'unavailable' };

@Injectable()
export class NetworkInterceptor implements HttpInterceptor {
  private tokenRequestInProgress: Promise<NativeTokenState> | null = null;
  private forcedTokenRequestInProgress: Promise<NativeTokenState> | null = null;
  private bridgeReady: Promise<any | null> | null = null;

  constructor(
    private utils: UtilsService,
    private service: PayarenaService,
    private tokenRefreshService: TokenRefreshService,
    private loadingService: LoadingService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Endpoints not to set bearer token to
    if (this.excludedEndpoints(request)) {
      return next.handle(request).pipe(
        distinctUntilChanged(),
        catchError((error) => this.handleServiceError(error, request)),
        finalize(() => this.loadingService.hideLoading())
      );
    }

    // The Flutter app can answer this from its cache in milliseconds. Asking before
    // each request means a token refreshed elsewhere in the native app is used here.
    return from(this.getTokenFromFlutter(false)).pipe(
      switchMap((nativeToken) => {
        if (nativeToken.status === 'requiresLogin') {
          return this.stopForNativeLogin();
        }
        if (nativeToken.status === 'notAllowed') {
          return this.stopForUntrustedPage();
        }

        const authorizedRequest = this.withAccessToken(
          request,
          nativeToken.status === 'token' ? nativeToken.token : undefined,
        );

        return next.handle(authorizedRequest).pipe(
          distinctUntilChanged(),
          catchError((error: HttpErrorResponse) => {
            // Try the API refresh token first. If it is unavailable or rejected,
            // retain the native Flutter refresh flow as a fallback.
            if (error.status === 401 && !request.context.get(NATIVE_TOKEN_RETRY)) {
              return this.getRefreshedAccessToken().pipe(
                switchMap((token) => token
                  ? of({ status: 'token', token } as NativeTokenState)
                  : from(this.getTokenFromFlutter(true)),
                ),
                switchMap((refreshedToken) => {
                  if (refreshedToken.status === 'requiresLogin') return this.stopForNativeLogin();
                  if (refreshedToken.status === 'notAllowed') return this.stopForUntrustedPage();
                  if (refreshedToken.status !== 'token') {
                    return this.handleServiceError(error, authorizedRequest);
                  }

                  const retryRequest = this.withAccessToken(
                    request.clone({ context: request.context.set(NATIVE_TOKEN_RETRY, true) }),
                    refreshedToken.token,
                  );
                  return next.handle(retryRequest).pipe(
                    catchError((retryError) => this.handleServiceError(retryError, retryRequest)),
                  );
                }),
              );
            }

            return this.handleServiceError(error, authorizedRequest);
          }),
        );
      }),
      finalize(() => this.loadingService.hideLoading()),
    );
  }

  private withAccessToken(
    request: HttpRequest<unknown>,
    token?: string,
  ): HttpRequest<unknown> {
    const accessToken = token ?? localStorage.getItem(this.utils.STORAGE_KEYS.TOKEN);
    // Do not turn a missing token into the literal value "Bearer null".
    return accessToken
      ? request.clone({
          headers: request.headers.set('Authorization', `Bearer ${accessToken}`),
        })
      : request;
  }

  private getRefreshedAccessToken(): Observable<string | null> {
    return this.tokenRefreshService.refreshAccessToken();
  }

  private getTokenFromFlutter(force: boolean): Promise<NativeTokenState> {
    const inProgress = force
      ? this.forcedTokenRequestInProgress
      : this.tokenRequestInProgress;

    if (inProgress) return inProgress;

    const request = this.requestTokenFromFlutter(force).finally(() => {
      if (force) this.forcedTokenRequestInProgress = null;
      else this.tokenRequestInProgress = null;
    });

    if (force) this.forcedTokenRequestInProgress = request;
    else this.tokenRequestInProgress = request;

    return request;
  }

  private async requestTokenFromFlutter(force: boolean): Promise<NativeTokenState> {
    const bridge = await this.waitForFlutterBridge();

    if (!bridge?.callHandler) return { status: 'unavailable' };

    try {
      // Flutter reports retry:true for a temporary IDP/network problem. It says the
      // user is still signed in, so wait and retry rather than clearing their session.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const result = (await bridge.callHandler(
          'getAccessToken',
          ...(force ? [{ force: true }] : []),
        )) as NativeTokenResult | null;

        if (result?.accessToken) {
          localStorage.setItem(this.utils.STORAGE_KEYS.TOKEN, result.accessToken);
          if (result.refreshToken) {
            localStorage.setItem(this.utils.STORAGE_KEYS.REFRESH_TOKEN, result.refreshToken);
          }
          if (result.identityId) {
            localStorage.setItem(this.utils.STORAGE_KEYS.IDENTITY_ID, result.identityId);
          }
          if (result.identityToken) {
            localStorage.setItem(this.utils.STORAGE_KEYS.IDENTITY_TOKEN, result.identityToken);
          }
          return { status: 'token', token: result.accessToken };
        }

        if (result?.requiresLogin) return { status: 'requiresLogin' };
        if (result?.error === 'not_allowed') {
          console.error('Flutter rejected this page as an untrusted Payarena page');
          return { status: 'notAllowed' };
        }

        if (!result?.retry) return { status: 'unavailable' };
        if (attempt < 2) await this.delay(2000);
      }

      return { status: 'unavailable' };
    } catch (error) {
      console.error('Unable to obtain a fresh token from Flutter', error);
      return { status: 'unavailable' };
    }
  }

  private waitForFlutterBridge(): Promise<any | null> {
    const existingBridge = (window as any).flutter_inappwebview;
    if (existingBridge?.callHandler) return Promise.resolve(existingBridge);
    if (this.bridgeReady) return this.bridgeReady;

    this.bridgeReady = new Promise((resolve) => {
      const onReady = () => finish((window as any).flutter_inappwebview || null);
      const timeout = window.setTimeout(() => finish(null), 2000);
      const finish = (bridge: any | null) => {
        window.removeEventListener('flutterInAppWebViewPlatformReady', onReady);
        window.clearTimeout(timeout);
        resolve(bridge);
      };

      window.addEventListener('flutterInAppWebViewPlatformReady', onReady, {
        once: true,
      });
    });

    return this.bridgeReady;
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  private stopForNativeLogin(): Observable<never> {
    // Flutter is already navigating to its login screen. Intentionally avoid an
    // Angular logout toast or a second login route.
    return throwError(() => new Error('Native session has ended'));
  }

  private stopForUntrustedPage(): Observable<never> {
    return throwError(() => new Error('This page is not trusted by the Flutter app'));
  }

  private excludedEndpoints(req: HttpRequest<any>): boolean {
    return (
      req.url.includes('/apimall') ||
      req.url.includes('/payarenamall') ||
      req.url.includes('/messagelog') ||
      req.url.includes('/account/login') ||
      req.url.includes('/ecommerce')
    );
  }

  private handleServiceError(
    err: HttpErrorResponse,
    request: HttpRequest<unknown>
  ) {
     const token = localStorage[this.utils.STORAGE_KEYS.TOKEN];
    const status = err.status;
    const errMessge = err?.error?.Message ?? err?.error?.detail ?? null

    switch (status) {
      case 401:
        this.utils.presentToast(
          errMessge|| 'You are unauthorized, please logout and login again'
        );
        break;
      case 400:
        this.utils.presentToast(errMessge || 'Resource not found');
        break;
      case 403:
        this.utils.presentToast(errMessge  || 'Forbidden To Access Resource');
        break;
      case 404:
        this.utils.presentToast(errMessge || 'Resource Not Found');
        break;
      case 0:
        this.utils.presentToast(
          errMessge  || 'Please Check Your Network Connection'
        );
        break;
      case 503:
        this.utils.presentToast(
          errMessge || 'Service Unavailable, Please Try Again Later'
        );
        break;
      case 504:
        this.utils.presentToast(errMessge  || 'Gateway Timeout');
        break;
      case 500:
        this.utils.presentToast(
          errMessge  || 'Internal Server Error, please contact support team'
        );
        break;
      default:
        break;
    }

    // const messageToServer = {
    //   Request: request,
    //   Response: errMessge,
    //   Date: new Date(),
    //   StatusCode: err.status,
    // };

    //this.sendLogToServer(messageToServer); // Logging to Payarena Log server
    return throwError(() => err);
  }

  private sendLogToServer(messageToServer) {
    this.service
      .sendErrorLogToServer(messageToServer)
      .pipe(
        retry(1),
        catchError((err) => of(err))
      )
      .subscribe();
  }
}
